import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import * as api from "../api/client";
import { construirPayload } from "../domain/contracts";
import { calcularCotizacion, validarCotizacion } from "../domain/pricing";
import { moneda } from "../domain/model";
import type { Cotizacion, ResultadoCalculo, Vendedor } from "../domain/types";
import { useConfirmar } from "../components/Dialogos";
import { useEditor } from "./editor";

export type Guardada = {
  c: Cotizacion;
  r: ResultadoCalculo;
  saved: Awaited<ReturnType<typeof api.guardar>>;
};

// Guardar la cotización en pantalla en el historial: valida, recalcula con el
// precio autoritativo de n8n, pide confirmar si difiere de la vista previa y
// guarda (o actualiza / crea la revisión -R). Lo comparten el botón "Guardar"
// de la barra superior y los de AccionesCotizacion (guardar y entregar), para
// que hagan exactamente lo mismo. Devuelve null si el vendedor cancela.
export function useGuardarCotizacion() {
  const client = useQueryClient(),
    confirmar = useConfirmar();
  return useCallback(
    async (
      vendedor: Vendedor,
      {
        entregar = false,
        onEstado,
      }: { entregar?: boolean; onEstado?: (texto: string) => void } = {},
    ): Promise<Guardada | null> => {
      const c = structuredClone(useEditor.getState().cotizacion),
        errores = validarCotizacion(c);
      if (errores.length) throw new Error(errores.join(" "));
      // Guardar y entregar son operaciones distintas para el backend
      // (entregar fija la versión del cliente), así que no comparten clave.
      const key = useEditor
        .getState()
        .claveGuardado(JSON.stringify(c) + (entregar ? "|entregar" : ""));
      const payload = construirPayload(c, vendedor, key, entregar);
      onEstado?.("Validando precios…");
      const r = await api.calcular(payload);
      if (
        Math.abs(r.total_general_interno - calcularCotizacion(c).total) >
          0.01 &&
        !(await confirmar({
          titulo: "El precio validado no coincide",
          mensaje: `El precio validado es ${moneda(r.total_general_interno)}, distinto de la vista previa.`,
          confirmar: "Continuar con este precio",
        }))
      )
        return null;
      onEstado?.("Guardando cotización…");
      const saved = await api.guardar(
        payload,
        vendedor,
        c.cliente.nombre,
        r.total_general_interno,
      );
      useEditor.getState().guardada(saved);
      void client.invalidateQueries({ queryKey: ["historial"] });
      return { c, r, saved };
    },
    [client, confirmar],
  );
}
